import React, { useState, useCallback, useEffect } from 'react';
import Cropper, { Area } from 'react-easy-crop';

import "./ImageUploader.scss";

interface IProps {
  label: string;
  currentImageUrl?: string;
  aspectRatio?: number; // 1 for square, 16/9 for wide, etc.
  circularCrop?: boolean;
  onImageChange: (croppedImageFile: File | null) => void;
  maxWidth?: number;
  maxHeight?: number;
  useTrueSize?: boolean;
  /**
   * Let the parent own the trigger and preview. Hides the label, upload button and current
   * image; the parent opens the file picker with `inputRef.current?.click()` and only the
   * crop step renders here.
   */
  hideControls?: boolean;
  inputRef?: React.RefObject<HTMLInputElement>;
  /**
   * Lowest zoom allowed. Below 1 the whole image can sit inside the crop box with
   * white space around it (useful for diagrams); 1 keeps the image covering the box.
   */
  minZoom?: number;
  /** Fires when the crop step opens (true) and closes (false). */
  onCroppingChange?: (cropping: boolean) => void;
}

const ImageUploader: React.FC<IProps> = ({
  label,
  currentImageUrl,
  aspectRatio = 1,
  circularCrop = false,
  onImageChange,
  maxWidth = 300,
  maxHeight = 300,
  useTrueSize = false,
  hideControls = false,
  inputRef,
  minZoom = 1,
  onCroppingChange
}) => {
  const canZoomOut = minZoom < 1;

  const [imgSrc, setImgSrc] = useState<string>();
  const [isSelectingImage, setIsSelectingImage] = useState<boolean>(false);

  useEffect(() => {
    onCroppingChange?.(isSelectingImage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSelectingImage]);
  
  // react-easy-crop state
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);

  const onSelectFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0];
      const reader = new FileReader();
      reader.addEventListener('load', () => {
        setImgSrc(reader.result?.toString() || '');
        setIsSelectingImage(true);
        // Reset crop and zoom for new image
        setCrop({ x: 0, y: 0 });
        setZoom(1);
      });
      reader.readAsDataURL(file);
      // Let the same file be picked again after a cancel.
      e.target.value = '';
    }
  };

  const onCropComplete = useCallback((_croppedArea: Area, croppedAreaPixels: Area) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  const createCroppedImage = useCallback(
    async (imageSrc: string, pixelCrop: Area): Promise<File> => {
      const image = await createImage(imageSrc);
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');

      if (!ctx) {
        throw new Error('No 2d context');
      }

      if (useTrueSize) {
        // For true size, output at the natural resolution of the cropped area
        canvas.width = Math.floor(pixelCrop.width);
        canvas.height = Math.floor(pixelCrop.height);
        
        // JPEG has no alpha; transparent PNG areas would otherwise come out black.
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        ctx.imageSmoothingQuality = 'high';

        ctx.drawImage(
          image,
          pixelCrop.x,
          pixelCrop.y,
          pixelCrop.width,
          pixelCrop.height,
          0,
          0,
          pixelCrop.width,
          pixelCrop.height
        );
      } else {
        // For non-true size, use pixel ratio for sharper display
        const pixelRatio = window.devicePixelRatio;
        
        // Calculate output size maintaining aspect ratio but respecting maxWidth/maxHeight
        let outputWidth = pixelCrop.width;
        let outputHeight = pixelCrop.height;
        
        if (outputWidth > maxWidth || outputHeight > maxHeight) {
          const scale = Math.min(maxWidth / outputWidth, maxHeight / outputHeight);
          outputWidth = outputWidth * scale;
          outputHeight = outputHeight * scale;
        }
        
        canvas.width = outputWidth * pixelRatio;
        canvas.height = outputHeight * pixelRatio;

        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        ctx.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0);
        ctx.imageSmoothingQuality = 'high';

        ctx.drawImage(
          image,
          pixelCrop.x,
          pixelCrop.y,
          pixelCrop.width,
          pixelCrop.height,
          0,
          0,
          outputWidth,
          outputHeight
        );
      }

      return new Promise((resolve, reject) => {
        canvas.toBlob(
          (blob) => {
            if (!blob) {
              reject(new Error('Canvas is empty'));
              return;
            }
            const file = new File([blob], 'cropped-image.jpg', {
              type: 'image/jpeg',
            });
            resolve(file);
          },
          'image/jpeg',
          0.9
        );
      });
    },
    [useTrueSize, maxWidth, maxHeight]
  );

  const handleSaveCrop = async () => {
    if (imgSrc && croppedAreaPixels) {
      try {
        const croppedImageFile = await createCroppedImage(imgSrc, croppedAreaPixels);
        onImageChange(croppedImageFile);
      } catch (e) {
        console.error('Error cropping image:', e);
      }
    }
    setIsSelectingImage(false);
    setImgSrc(undefined);
    setCroppedAreaPixels(null);
  };

  const handleCancelCrop = () => {
    setIsSelectingImage(false);
    setImgSrc(undefined);
    setCroppedAreaPixels(null);
    setCrop({ x: 0, y: 0 });
    setZoom(1);
  };

  return (
    <div className={`ImageUploader ${circularCrop ? 'circular' : 'rectangular'}`}>
      <div className="uploaderSection">
        {hideControls ? (
          <input ref={inputRef} type="file" accept="image/*" onChange={onSelectFile} style={{ display: 'none' }} />
        ) : (
          <div className="uploaderHeader">
            <p className="label">{label}</p>
            {/* Upload Button */}
            <label className="uploadButton">
              <input ref={inputRef} type="file" accept="image/*" onChange={onSelectFile} />
              <span>{(currentImageUrl || imgSrc) ? 'Change Image' : 'Upload Image'}</span>
            </label>
          </div>
        )}

        {/* Current Image Preview */}
        {!hideControls && currentImageUrl && !isSelectingImage && (
          <div className="currentImageWrap">
            <img src={currentImageUrl} alt="Current" className="currentImage" />
          </div>
        )}

        {/* Crop Interface */}
        {imgSrc && isSelectingImage && (
          <div className="cropInterface">
            <div className="cropContainer">
              <Cropper
                image={imgSrc}
                crop={crop}
                zoom={zoom}
                aspect={aspectRatio}
                cropShape={circularCrop ? 'round' : 'rect'}
                showGrid={!circularCrop}
                minZoom={minZoom}
                restrictPosition={!canZoomOut}
                onCropChange={setCrop}
                onZoomChange={setZoom}
                onCropComplete={onCropComplete}
              />
            </div>
            
            <div className="zoomControl">
              <label className="zoomLabel">
                <span className="zoomIcon">🔍</span>
                <span>Zoom</span>
              </label>
              <input
                type="range"
                min={minZoom}
                max={3}
                step={0.1}
                value={zoom}
                onChange={(e) => setZoom(Number(e.target.value))}
                className="zoomSlider"
              />
              <span className="zoomValue">{Math.round(zoom * 100)}%</span>
            </div>
            
            <div className="cropActions">
              <button type="button" onClick={handleCancelCrop} className="cancelButton">
                Cancel
              </button>
              <button type="button" onClick={handleSaveCrop} className="saveButton">
                Save
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Helper function to load an image
const createImage = (url: string): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.addEventListener('load', () => resolve(image));
    image.addEventListener('error', (error) => reject(error));
    image.crossOrigin = 'anonymous';
    image.src = url;
  });

export default ImageUploader;
